import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Send, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { submitSuggestion } from "@/lib/adminApi";

export default function SuggestRecipe() {
  const { member, displayName } = useAuth();
  const nav = useNavigate();
  const [recipeName, setRecipeName] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (recipeName.trim().length < 2) return toast.error("Ponle nombre al plato");
    if (reason.trim().length < 5) return toast.error("Cuéntanos por qué te gustaría");
    setSending(true);
    try {
      await submitSuggestion({
        recipe_name: recipeName.trim(),
        reason: reason.trim(),
        notes: notes.trim(),
        from_member: member,
        from_display: displayName || member,
      });
      setSent(true);
      toast.success("¡Enviada! Papi va a revisarla 💛");
    } catch (e) {
      toast.error("No se pudo enviar: " + (e.message || e));
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen paper-texture iphone-safe-top">
      <header className="sticky top-0 z-30 bg-paper/95 backdrop-blur-md border-b border-line">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link data-testid="suggest-back-btn" to="/cocina" className="p-2 rounded-full bg-cream border border-line">
            <ChevronLeft className="w-5 h-5 text-cocoa" />
          </Link>
          <div className="text-center">
            <p className="eyebrow text-[10px]">Sugerir una receta</p>
            <h1 className="font-display italic text-cocoa text-2xl leading-tight">Papi, agrega esto…</h1>
          </div>
          <div className="w-9 h-9" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 pt-6 pb-24">
        {sent ? (
          <div className="bg-cream border-2 border-amber-deep/40 rounded-2xl p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-deep/15 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-amber-deep" />
            </div>
            <h2 className="font-display italic text-cocoa text-3xl mb-2">¡Enviada!</h2>
            <p className="font-serif text-cocoa-soft italic mb-6">
              Papi ya recibió tu sugerencia. Cuando la revise y le encuentre buena, va a subirla al recetario y te avisa.
            </p>
            <div className="flex flex-col gap-2 max-w-xs mx-auto">
              <button
                data-testid="suggest-another-btn"
                onClick={() => { setRecipeName(""); setReason(""); setNotes(""); setSent(false); }}
                className="btn-ghost"
              >Sugerir otra</button>
              <button data-testid="suggest-back-home-btn" onClick={() => nav("/cocina")} className="btn-primary">
                Volver al inicio
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-cream border border-line rounded-2xl p-6">
            <p className="font-serif italic text-cocoa-soft mb-5">
              Si hay un plato familiar que quieres que Papi cocine y suba al recetario, cuéntanoslo. Papi lo va a leer y armar la receta con su tono, y después la publica.
            </p>

            <label className="block mb-4">
              <span className="text-xs italic text-cocoa-soft mb-1 block">Nombre del plato *</span>
              <input
                data-testid="suggest-name-input"
                type="text"
                value={recipeName}
                onChange={(e) => setRecipeName(e.target.value)}
                placeholder='Ej: "Kibbeh de la abuela", "Locos con mayo casera"…'
                className="w-full px-4 py-3 rounded-lg bg-paper border border-line text-cocoa font-serif focus:outline-none focus:ring-2 focus:ring-amber-deep/40"
                maxLength={120}
              />
            </label>

            <label className="block mb-4">
              <span className="text-xs italic text-cocoa-soft mb-1 block">¿Por qué te gustaría tenerla? *</span>
              <textarea
                data-testid="suggest-reason-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                placeholder="Contarnos algún recuerdo, ocasión especial o dónde lo comiste — a Papi le encanta el contexto."
                className="w-full px-4 py-3 rounded-lg bg-paper border border-line text-cocoa font-serif focus:outline-none focus:ring-2 focus:ring-amber-deep/40 resize-y"
                maxLength={800}
              />
            </label>

            <label className="block mb-6">
              <span className="text-xs italic text-cocoa-soft mb-1 block">Notas o link (opcional)</span>
              <textarea
                data-testid="suggest-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Si tienes la receta, pégala. O el link donde la viste. Cualquier cosa que ayude a Papi."
                className="w-full px-4 py-3 rounded-lg bg-paper border border-line text-cocoa font-serif text-sm focus:outline-none focus:ring-2 focus:ring-amber-deep/40 resize-y"
                maxLength={2000}
              />
            </label>

            <button
              data-testid="suggest-submit-btn"
              onClick={submit}
              disabled={sending || recipeName.trim().length < 2 || reason.trim().length < 5}
              className="btn-primary w-full"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Enviando…" : "Enviar a Papi"}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
